import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, Flex } from 'antd';
import * as R from 'ramda';

import DetailSpinner from '@totem/components/common/DetailSpinner';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { mainMenuPlacement } from '@totem/components/ui_components/constants';
import MenuContainer from '@totem/components/ui_components/menus/menuContainer';
import {
  mainMenuDisplayOptions,
  themeOptions,
} from '@totem/components/ui_components/menus/types';
import accountUtilities, {
  getAccountPayloadSync,
  getToken,
  getTokenExpirationDate,
  setToken,
} from '@totem/components/userContext/accountUtilities';
import { TokenRefreshResponse } from '@totem/components/userContext/auth/types';
import ContractedServicesContainer from '@totem/components/userContext/contractedServicesContext/ContractedServicesContainer';
import EulaDialog from '@totem/components/userContext/eulaDialog/EulaDialog';
import OrganizationContainer from '@totem/components/userContext/organizationContext/OrganizationContainer';
import UserPersistenceContainer from '@totem/components/userContext/persistence/UserPersistenceContainer';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { darkThemeData, lightThemeData } from '@totem/styles/theme';
import { LoginProfile, UserProfile } from '@totem/types/user';
import api from '@totem/utilities/api';
import authUtilities from '@totem/utilities/authUtilities';
import { isNotNull, isNull } from '@totem/utilities/common';
import {
  LOGIN_PROFILE_ENDPOINT,
  TOKEN_REFRESH_ENDPOINT,
} from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import {
  consumePostLoginRedirect,
  setPostLoginRedirect,
} from '@totem/utilities/loginRedirect';

type Props = {
  exact?: boolean;
  path?: string;
  component: any;
  roleSet: string[];
};

const AuthorizedRoute = ({ component: Component, roleSet }: Props) => {
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [doRefreshToken, setDoRefreshToken] = useState<boolean>(false);
  const [isTokenRefreshSending, setIsTokenRefreshSending] =
    useState<boolean>(false);
  const [error, setError] = useState<string>(null);
  const [isOSDark, setIsOSDark] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  const navigate = useNavigate();
  const location = useLocation();

  const [userProfile, setUserProfile] = useState<UserProfile>(null);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [tokenTTL, setTokenTTL] = useState<number>(0);
  const [tokenRefreshCount, setTokenRefreshCount] = useState<number>(0);
  const [showTooltips, setShowTooltips] = useState<boolean>(false);
  const [eulaAcknowledged, setEulaAcknowledged] = useState<boolean>(false);
  const isOrgRouteSwitching = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (ev: MediaQueryListEvent) => setIsOSDark(ev.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const mainMenuLocation = useMemo(() => {
    let menuLocation = mainMenuPlacement;

    if (
      isNotNull(userProfile) &&
      isNotNull(userProfile.preferences) &&
      isNotNull(userProfile.preferences.menuLocation)
    ) {
      menuLocation = userProfile.preferences.menuLocation;
    }

    if (!mainMenuDisplayOptions.includes(menuLocation)) {
      menuLocation = mainMenuPlacement;
    }

    return menuLocation;
  }, [userProfile]);

  const preferredTheme = useMemo(() => {
    let selectedTheme = 'light';

    if (
      isNotNull(userProfile) &&
      isNotNull(userProfile.preferences) &&
      isNotNull(userProfile.preferences.theme)
    ) {
      selectedTheme = userProfile.preferences.theme;
    }

    if (!themeOptions.includes(selectedTheme)) {
      selectedTheme = 'light';
    }

    if (selectedTheme === 'os') {
      selectedTheme = isOSDark ? 'dark' : 'light';
    }

    return selectedTheme;
  }, [userProfile]);

  const retrieveUserId = () => {
    if (isNotNull(userProfile)) {
      return userProfile.id;
    }

    if (!getToken()) {
      return 'LOGIN';
    }

    // TODO: Remove @ts-ignore : Token
    // @ts-ignore
    return getAccountPayloadSync().id;
  };

  const setApiToken = (token: string | null, source: string) => {
    setUserToken(token); // Context

    setToken(token, `AuthorizedRoute.setApiToken.${source}`); // Cookie

    setTokenRefreshCount((prevState) => prevState + 1);
  };

  const handleRefreshToken = () => {
    if (!doRefreshToken) {
      setDoRefreshToken(true);
    }
  };

  const checkTokenBelowHalfTTL = (tokenExpiration: Date) => {
    if (tokenTTL > 0 && !isTokenRefreshSending && !doRefreshToken) {
      const now = new Date();
      const minutes =
        Math.abs(tokenExpiration.getTime() - now.getTime()) / 60000;
      if (minutes < tokenTTL / 2) {
        logger.debug(
          `TOKEN REFRESH: Minutes Until Token Expiration: ${minutes} / TTL: ${
            tokenTTL
          }`,
        );

        handleRefreshToken();
      }
    }
  };

  // Expired token: go to login, and come back to this page afterwards (see AuthCallback)
  const redirectToLogin = () => {
    setPostLoginRedirect(
      `${location.pathname}${location.search}${location.hash}`,
    );

    navigate('/login');
  };

  const getApiToken = () => {
    const now = new Date();
    if (isNotNull(userToken) && userToken !== '') {
      const expiration = getTokenExpirationDate(userToken);

      if (isNotNull(expiration)) {
        if (expiration.valueOf() < now.valueOf()) {
          redirectToLogin();
        }
        checkTokenBelowHalfTTL(expiration);
      }

      return userToken;
    }
    const tokenToReturn = getToken();
    const cookieTokenExpiration = getTokenExpirationDate(tokenToReturn);

    if (isNotNull(cookieTokenExpiration)) {
      if (cookieTokenExpiration.valueOf() < now.valueOf()) {
        redirectToLogin();
      }
      checkTokenBelowHalfTTL(cookieTokenExpiration);
    }

    setRefreshData(true);
    return tokenToReturn;
  };

  const getSoftApiToken = () => {
    if (isNotNull(userToken) && userToken !== '') {
      return userToken;
    }
    const tokenToReturn = getToken();
    return tokenToReturn;
  };

  const setProfileAndToken = (
    profile: UserProfile,
    newToken: string,
    source: string,
  ) => {
    // TODO: Remove Debug Logic
    logger.debug(
      `setProfileAndToken: ${profile.id} / ${profile.organization.id} / ${source}`,
    );
    if (newToken !== '') {
      setApiToken(newToken, 'AuthorizedRoute.setProfileAndToken');

      setEulaAcknowledged(profile.preferences?.eulaAcknowledged === true);

      setUserProfile(profile);

      const expiration = accountUtilities.getTokenExpirationDate(newToken);
      if (isNotNull(expiration)) {
        logger.debug(
          `setProfileAndToken.Expiration: ${expiration.toISOString()} (${expiration.valueOf()})`,
        );
      }

      api.setAuthToken();
    }
  };

  const getOrgIdFromPath = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] !== 'dashboard' || parts.length < 2) {
      return null;
    }
    const candidate = parts[1];
    return /^[a-f0-9]{24}$/i.test(candidate) ? candidate : null;
  };

  const getOrgRouteTarget = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    const restParts = parts.slice(2);
    const targetPath = `/dashboard${
      restParts.length ? `/${restParts.join('/')}` : ''
    }`;
    return `${targetPath}${location.search}${location.hash}`;
  };

  const userId = retrieveUserId();

  const handleSetTTL = (newTTL: number) => {
    logger.debug(`Setting TTL: ${newTTL}`);

    setTokenTTL(newTTL);
  };

  const switchOrganizationFromRoute = (organizationId: string) => {
    if (isOrgRouteSwitching.current) {
      return;
    }

    if (isNull(userProfile) || isNull(userProfile.organizations)) {
      // No Permissions
      navigate('/');
    }
    const foundUserOrganization = userProfile.organizations.find(
      (chk) => chk.id === organizationId,
    );
    if (isNull(foundUserOrganization)) {
      // No Permissions
      navigate('/');
    }

    isOrgRouteSwitching.current = true;

    fetch(`${LOGIN_PROFILE_ENDPOINT}/${userId}/${organizationId}/switch`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          setError(res.statusText);
        }
        return res.json();
      })
      .then((result: LoginProfile) => {
        if (result.token) {
          if (result.ttlMinutes > 0) {
            handleSetTTL(result.ttlMinutes);
          }
          setApiToken(
            result.token,
            'AuthorizedRoute.switchOrganizationFromRoute',
          );
        }
        setUserProfile(
          R.over(
            R.lensPath(['organizations']),
            R.sortBy(R.prop('name')),
            result.profile,
          ),
        );

        consumePostLoginRedirect();

        navigate(getOrgRouteTarget(), { replace: true });
      })
      .finally(() => {
        isOrgRouteSwitching.current = false;
      });
  };

  const onCompleted = (user: UserProfile) => {
    if (isNull(user) || isNull(user.organizations)) {
      navigate('/login');
      return;
    }
    api.setAuthToken();

    setEulaAcknowledged(user.preferences?.eulaAcknowledged === true);

    setUserProfile(
      // sort user organizations by name
      R.over(R.lensPath(['organizations']), R.sortBy(R.prop('name')))(user),
    );

    const organizations = !user.organizations
      ? []
      : user.organizations.filter((org) => org.isActive);

    if (!user.isEmailVerified) {
      navigate('/unverified-email');
    } else if (R.isEmpty(organizations)) {
      navigate('/login');
      //navigate('/no-access');
    } else if (!user.id) {
      navigate('/login');
    } else if (!user.organization || user.organization.id === EMPTY_ID) {
      const orgIdFromPath = getOrgIdFromPath();
      if (orgIdFromPath) {
        switchOrganizationFromRoute(orgIdFromPath);
        return;
      }
      navigate('/switch-organization');
    } else if (!authUtilities.minimumRequiredRole(user, roleSet)) {
      // const endpoint = authUtilities.isTenantUser(user)
      //   ? '/tenant-dashboard'
      //   : authUtilities.isVendorUser(user)
      //     ? '/vendor-dashboard'
      //     : '/dashboard';

      const endpoint = authUtilities.isTenantUser(user)
        ? '/tenant-dashboard'
        : '/dashboard';

      navigate(endpoint);
    } else if (location.pathname !== '/switch-organization') {
      // Leave the saved link in place on the org picker: deep links without an org id in the path
      // (e.g. an emailed /dashboard/ticket/:id/claim link) are routed through /switch-organization after
      // login, and the picker consumes and navigates to the link once an organization is chosen.
      consumePostLoginRedirect();
    }
  };

  // Use Effect #1
  useEffect(() => {
    if (userId === 'LOGIN') {
      const nextPath = `${location.pathname}${location.search}${location.hash}`;
      setPostLoginRedirect(nextPath);
    }
  }, [userId, location.pathname, location.search, location.hash]);

  // Use Effect #2
  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      fetch(`${LOGIN_PROFILE_ENDPOINT}/${userId}/${EMPTY_ID}/switch`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => {
          if (res.status >= 400) {
            setError(res.statusText);
          }
          return res.json();
        })
        .then((result: LoginProfile) => {
          if (result.ttlMinutes > 0) {
            handleSetTTL(result.ttlMinutes);
          }
          setApiToken(result.token, 'AuthorizedRoute.useEffect#2');

          onCompleted(result.profile);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  // Use Effect #3
  useEffect(() => {
    if (
      userProfile?.id &&
      !authUtilities.minimumRequiredRole(userProfile, roleSet)
    ) {
      // const endpoint = authUtilities.isTenantUser(userProfile)
      //   ? '/tenant-dashboard'
      //   : authUtilities.isVendorUser(userProfile)
      //     ? '/vendor-dashboard'
      //     : '/dashboard';

      const endpoint = authUtilities.isTenantUser(userProfile)
        ? '/tenant-dashboard'
        : '/dashboard';

      navigate(endpoint);
    }
  }, []);

  // Us Effect #4
  useEffect(() => {
    if (isNotNull(error)) {
      navigate('/login');
    }
    if (typeof error !== 'undefined') {
      if (error) {
        navigate('/login');
      }
    }
  }, [error]);

  // Use Effect #5
  useEffect(() => {
    if (doRefreshToken) {
      setDoRefreshToken(false);

      if (isTokenRefreshSending) {
        return;
      }
      setIsTokenRefreshSending(true);

      fetch(`${TOKEN_REFRESH_ENDPOINT}`, {
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        }),
      })
        .then((res) => {
          setIsTokenRefreshSending(false);
          if (res.status >= 400) {
            throw Error('Bad Response');
          }
          return res;
        })
        .then((res) => res.json())
        .then((result: TokenRefreshResponse) => {
          if (result.token !== '') {
            setApiToken(result.token, 'AuthorizedRoute.refreshToken');
          }
        });
    }
  }, [doRefreshToken]);

  if (userId === 'LOGIN') {
    return <Navigate to="/login" replace />;
  }

  if (!userProfile?.id) {
    return (
      <Flex justify={'center'} align={'center'} style={{ height: '100vh' }}>
        <DetailSpinner size={96} />
      </Flex>
    );
  }

  return (
    <UserProfileContext.Provider
      value={{
        loading: isLoading,
        refetch: () => setRefreshData(true),
        refreshToken: handleRefreshToken,
        setProfile: setUserProfile,
        setProfileAndToken,
        userProfile,
        showTooltips,
        setShowTooltips,
        setApiToken,
        getApiToken,
        getSoftApiToken,
        setTokenTTL: handleSetTTL,
        tokenTTL,
        tokenRefreshCount,
        mainMenuLocation,
        preferredTheme,
      }}
    >
      <ContractedServicesContainer>
        <ConfigProvider
          theme={preferredTheme === 'dark' ? darkThemeData : lightThemeData}
        >
          {!eulaAcknowledged &&
            location.pathname !== '/switch-organization' &&
            isNotNull(userProfile.organization) &&
            userProfile.organization.id !== EMPTY_ID && (
              <EulaDialog onAcknowledged={() => setEulaAcknowledged(true)} />
            )}
          <UserPersistenceContainer>
            <MenuContainer>
              <OrganizationContainer>
                <div style={{ display: 'none' }}>
                  <span>User ID: {userProfile.id}</span>
                </div>
                <div style={{ height: '100%' }}>
                  <Component />
                </div>
              </OrganizationContainer>
            </MenuContainer>
          </UserPersistenceContainer>
        </ConfigProvider>
      </ContractedServicesContainer>
    </UserProfileContext.Provider>
  );
};

export default AuthorizedRoute;
